// M3 platforms & development wells (M3_SPEC §3): fixed and floating production structures bought per field plot,
// well slots (own plot + neighbours within 1 plot), rig slots, process capacity caps, storage, export modes, subsea
// tiebacks to a host, decommissioning liability. state.platforms[uid] (lazy).
import { eqDef, fieldDef, stat, yf, isAvailable, unitPrice } from '../defs.js';
import { inflate } from '../../data/cpi.js';
import { OFFSHORE, platformPrice, topsidesPrice, stepAt, LOGISTICS } from '../../data/m3/rules.js';
import { m3On, platforms, plotWater, maxWaterFor, envOf, serviceRate, problem, ensureRushKinds } from './opsutil.js';
import { m3Accident } from './opsincidents.js';
import { spend, notify, nextId, fmt$, log } from '../util.js';
import { startTimer } from '../timers.js';
import { chance, int } from '../../core/rng.js';

const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });
const no = (msg) => ({ ok: false, msg });
export const PLOT_MI = 1.5;                 // lateral size of a field plot (mi, est.) for tieback distances
export const NO_HOST_MAX_WATER_FT = 100;    // shallower wells produce through a caisson / well protector without a platform
export const SIMPLE_TOPSIDES = new Set(['lake', 'jacket_shallow']); // separators only: no process-plant surcharge
export const SUBSEA_JOB_DAYS = 10;           // dive support / ROV days per subsea installation

export const isPlatformDef = (def) => def?.cat === 'platform';
const pstats = (p) => eqDef(p?.defId)?.stats ?? {};

export function platformQuote(state, fieldId, plot, defId) {
  const def = eqDef(defId);
  const fdef = fieldDef(fieldId);
  if (!def || !isPlatformDef(def)) return { ok: false, reason: 'Not a platform.' };
  if (!fdef) return { ok: false, reason: 'No such field.' };
  const s = def.stats ?? {};
  const y = yf(state);
  const water = plotWater(fieldId, plot);
  const maxW = maxWaterFor(def, Math.floor(y));
  const harsh = envOf(fieldId)?.kind === 'harsh' && !s.harsh;
  const base = unitPrice(state, def);
  const structure = platformPrice(base, water, { refWaterFt: s.refWaterFt ?? 100, costDepthExp: s.costDepthExp ?? null, harsh });
  const topsides = SIMPLE_TOPSIDES.has(s.platformType) || !s.processBbld ? 0 : inflate(topsidesPrice(s.processBbld), OFFSHORE.platformCost.topsidesBaseYear, Math.min(y, 2025));
  let reason = null;
  if (!isAvailable(def, state)) reason = `${def.name} is not available until ${def.year}.`;
  else if (s.subsea) reason = 'Subsea trees are installed on a completed well (installSubsea).';
  else if (water < (s.minWater ?? 0)) reason = `Too shallow for a ${def.name} (needs ${s.minWater} ft; plot has ${Math.round(water)} ft).`;
  else if (water > maxW) reason = `Too deep for a ${Math.floor(y)} ${def.name} (rated to ${maxW} ft; plot has ${Math.round(water)} ft).`;
  else if (water <= 0 && !['lake'].includes(s.platformType)) reason = 'That plot is on dry land.';
  return { ok: !reason, reason, water, maxWater: maxW, structure, topsides, price: structure + topsides, harsh, buildDays: s.buildDays ?? 180, slots: s.slots ?? 0, rigSlots: s.rigSlots ?? 0,
    processBbld: s.processBbld ?? 0, storageBbl: s.storageBbl ?? 0, exportMode: s.exportMode ?? 'either', decommission: (structure + topsides) * OFFSHORE.decommissionFrac };
}

export function buildPlatformImpl(state, fieldId, plot, defId) {
  if (!m3On(state)) return no('Offshore platforms arrive after 1946.');
  const f = state.fields[fieldId];
  const p = f?.plots[plot];
  if (!p) return no('No such plot.');
  if (!p.owned) return no('Lease (or win the licence for) the plot first.');
  const q = platformQuote(state, fieldId, plot, defId);
  if (!q.ok) return no(q.reason);
  if (Object.values(platforms(state)).some((x) => x.fieldId === fieldId && x.plot === plot && x.status !== 'REMOVED' && !pstats(x).subsea)) return no('There is already a platform on that plot.');
  if (!spend(state, q.price, 'capex')) return no(`The ${eqDef(defId).name} costs ${fmt$(q.price)} installed; not enough cash.`);
  ensureRushKinds();
  const uid = nextId(state, 'pf');
  const t = startTimer(state, 'PLATFORM_BUILD', uid, q.buildDays, { platformUid: uid });
  platforms(state)[uid] = { uid, defId, fieldId, plot, status: 'BUILDING', timerId: t.id, price: Math.round(q.price), orderedDay: state.day, builtDay: null,
    waterDepthFt: q.water, slotsUsed: [], rigUids: [], hostOf: [], condition: 100, exportOk: null };
  notify(state, 'platform', { platformUid: uid, fieldId, phase: 'ordered', text: `${eqDef(defId).name} ordered for ${fieldDef(fieldId).name} (${fmt$(q.price)}, ${q.buildDays} days to fabricate and install).` });
  return ok(`${eqDef(defId).name} ordered: ${fmt$(q.price)}, installed in ${q.buildDays} days.`, { platformUid: uid });
}

// Daily: finish builds/repairs, platform opex.
export function tickPlatforms(state) {
  const P = state.platforms;
  if (!P) return;
  let opex = 0;
  for (const p of Object.values(P)) {
    const def = eqDef(p.defId);
    if ((p.status === 'BUILDING' || p.status === 'DAMAGED') && !state.timers[p.timerId]) {
      const was = p.status;
      p.status = 'INSTALLED';
      p.timerId = null;
      if (was === 'BUILDING') {
        p.builtDay = state.day;
        notify(state, 'platform', { platformUid: p.uid, fieldId: p.fieldId, phase: 'installed', pause: true, level: 'major', text: `${def?.name} installed at ${fieldDef(p.fieldId)?.name} in ${p.waterDepthFt} ft of water.` });
      } else { p.condition = Math.max(p.condition, 80); log(state, `${def?.name} at ${fieldDef(p.fieldId)?.name} repaired.`); }
    }
    if (p.status === 'REMOVED' || p.status === 'BUILDING') continue;
    opex += inflate(stat(def, 'opexPerDay', 0), def?.baseYear ?? def?.year ?? 1950, Math.min(yf(state), 2025));
  }
  if (opex > 0) spend(state, opex, 'opex', false);
}

export function platformStorage(state, fieldId) {
  const P = state.platforms;
  if (!P) return 0;
  let s = 0;
  for (const p of Object.values(P)) if (p.fieldId === fieldId && p.status !== 'BUILDING' && p.status !== 'REMOVED' && !pstats(p).subsea) s += pstats(p).storageBbl || pstats(p).processBbld || 0; // no storage: a day of surge vessels / line fill
  return s;
}

// platformStorage for every field in one pass (same per-field summation order). null when there are no platforms.
export function platformStorageByField(state) {
  const P = state.platforms;
  if (!P) return null;
  const out = new Map();
  for (const p of Object.values(P)) if (p.status !== 'BUILDING' && p.status !== 'REMOVED' && !pstats(p).subsea) out.set(p.fieldId, (out.get(p.fieldId) ?? 0) + (pstats(p).storageBbl || pstats(p).processBbld || 0));
  return out;
}

// Does the field's current transport suit the platform's export mode?
export function exportOk(state, p) {
  const mode = pstats(p).exportMode ?? 'either';
  if (mode === 'either') return true;
  const t = eqDef(state.fields[p.fieldId]?.transportId);
  if (!t) return false;
  const pipe = /pipeline|tapline|pipe/.test(t.id) && !stat(t, 'offshoreLoading', false);
  return mode === 'pipeline' ? pipe : !pipe;
}

// Offshore per-well opex multiple (DESIGN §5.5 ×10). A platform's shared costs are its own opexPerDay, so wells on a
// platform slot carry ×PLATFORM_WELL_OPEX_MULT; subsea and stand-alone (caisson) wells the full OFFSHORE.opexMult.
export const PLATFORM_WELL_OPEX_MULT = 3;
export const offshoreWellOpexMult = (well) => (well.platformUid && !well.subsea ? PLATFORM_WELL_OPEX_MULT : OFFSHORE.opexMult);

// Production hook (sim/production.js): platform process cap, export mode, damaged/unbuilt hosts. Returns the rate.
const tallies = new WeakMap();
export function m3ProdCap(state, well, q) {
  if (!well.offshore && !well.platformUid) return q;
  const p = well.platformUid ? state.platforms?.[well.platformUid] : null;
  if (!p) {
    if ((well.waterDepthFt ?? 0) > NO_HOST_MAX_WATER_FT) { problem(state, well, 'NO_HOST', `${well.waterDepthFt} ft of water: the well needs a platform slot or a subsea tree to a host before it can flow.`); return 0; }
    return q;
  }
  if (p.status !== 'INSTALLED') return 0;
  if (!exportOk(state, p)) { problem(state, well, 'NO_EXPORT', `the ${eqDef(p.defId)?.name} exports by ${pstats(p).exportMode === 'tanker' ? 'shuttle tanker' : 'pipeline'} only — set the field's transport.`); return 0; }
  let t = tallies.get(state);
  if (!t || t.day !== state.day) { t = { day: state.day, used: {} }; tallies.set(state, t); }
  const cap = pstats(p).processBbld ?? Infinity;
  const used = t.used[p.uid] ?? 0;
  const q2 = Math.max(0, Math.min(q, cap - used));
  t.used[p.uid] = used + q2;
  if (q2 < q - 1e-6 && well.prod) {
    well.prod.age -= 1 - (q > 0 ? q2 / q : 0); // excess stays in the reservoir: the decline clock runs at the produced rate
    if (well.problem?.type !== 'PROCESS_CAP') well.problem = { type: 'PROCESS_CAP', sinceDay: state.day, text: `the ${eqDef(p.defId)?.name} process plant is full (${cap.toLocaleString('en-US')} bbl/d).` };
  } else if (well.problem && ['PROCESS_CAP', 'NO_EXPORT', 'NO_HOST'].includes(well.problem.type)) well.problem = null;
  return q2;
}

export function connectPlatformImpl(state, platformUid, transportId, setTransport) {
  const p = state.platforms?.[platformUid];
  if (!p) return no('No such platform.');
  const t = eqDef(transportId);
  if (!t || t.cat !== 'transport') return no('Not a transport.');
  const mode = pstats(p).exportMode ?? 'either';
  const pipe = /pipeline|tapline|pipe/.test(t.id) && !stat(t, 'offshoreLoading', false);
  if (mode === 'pipeline' && !pipe) return no(`The ${eqDef(p.defId).name} has no storage or loading buoy: it exports by pipeline only.`);
  if (mode === 'tanker' && pipe) return no(`The ${eqDef(p.defId).name} exports by shuttle tanker only.`);
  const r = setTransport(state, p.fieldId, transportId);
  if (r.ok) p.exportOk = true;
  return r;
}

// Subsea tree on a completed offshore well, tied back to a host within tiebackMiles(y).
export function installSubseaImpl(state, wellId, defId = 'subsea_tree', hostUid = null) {
  const w = state.wells[wellId];
  if (!w) return no('No such well.');
  if (!m3On(state)) return no('Subsea completions arrive after 1946.');
  const def = eqDef(defId);
  if (!def || !stat(def, 'subsea', false)) return no('Not a subsea item.');
  if (defId === 'subsea_manifold') return no('Manifolds are bought with the host; install a subsea tree on each well.');
  if (!isAvailable(def, state)) return no(`${def.name} is not available until ${def.year}.`);
  if (!w.offshore) return no('Subsea trees are for offshore wells.');
  if (w.subsea) return no('The well already has a subsea tree.');
  if (!['PRODUCING', 'SHUT_IN', 'AT_TD', 'COMPLETING'].includes(w.status) || !w.reservoirId) return no('Complete the well on pay first.');
  const y = yf(state);
  const water = w.waterDepthFt ?? plotWater(w.fieldId, w.plot);
  if (water > maxWaterFor(def, Math.floor(y))) return no(`A ${Math.floor(y)} subsea tree is rated to ${maxWaterFor(def, Math.floor(y))} ft of water.`);
  const hosts = Object.values(state.platforms ?? {}).filter((p) => p.fieldId === w.fieldId && p.status === 'INSTALLED' && !pstats(p).subsea && (!hostUid || p.uid === hostUid));
  if (!hosts.length) return no('No installed host platform, FPS or FPSO in the field.');
  const gw = fieldDef(w.fieldId).grid?.w ?? 6;
  const dist = (p) => Math.hypot((p.plot % gw) - (w.plot % gw), Math.floor(p.plot / gw) - Math.floor(w.plot / gw)) * PLOT_MI;
  const limit = stepAt(stat(def, 'tiebackMiles', [[1961, 2]]), y) ?? 2;
  const cand = hosts.filter((p) => (p.hostOf?.length ?? 0) < (pstats(p).subseaHost ?? (pstats(p).slots ? 4 : 0))).sort((a, b) => dist(a) - dist(b));
  if (!cand.length) return no('No host with free subsea capacity.');
  const host = cand[0];
  const miles = Math.round(dist(host) * 10) / 10;
  if (miles > limit) return no(`The nearest host is ${miles} mi away; ${Math.floor(y)} tieback technology reaches ${limit} mi.`);
  // installation spread: divers to 1,000 ft, ROVs (1975+) deeper
  const dive = eqDef('dive_support'), rov = eqDef('rov_service');
  const useRov = water > (dive?.stats?.maxDiveFt ?? 1000) || (rov && isAvailable(rov, state) && y >= 1985);
  const spread = useRov ? rov : dive;
  if (!spread || !isAvailable(spread, state) || water > (spread.stats?.maxDiveFt ?? 1000)) return no(`No diving or ROV spread can work ${Math.round(water)} ft of water in ${Math.floor(y)}.`);
  const tree = platformPrice(unitPrice(state, def), water, { refWaterFt: def.stats.refWaterFt ?? 500, costDepthExp: def.stats.costDepthExp ?? 0.3 });
  const tieback = inflate(OFFSHORE.subseaTiebackCostPerMi, OFFSHORE.subseaTiebackBaseYear, Math.min(y, 2025)) * miles;
  const job = serviceRate(state, spread) * SUBSEA_JOB_DAYS;
  const cost = tree + tieback + job;
  if (!spend(state, cost, 'capex')) return no(`Tree, tieback and installation cost ${fmt$(cost)}.`);
  w.subsea = { defId, hostUid: host.uid, miles, day: state.day };
  w.platformUid = host.uid;
  (host.hostOf ??= []).push(w.id);
  if (!useRov) divingRisk(state, w);
  return ok(`Subsea tree on ${w.name}, tied back ${miles} mi to the ${eqDef(host.defId).name} (${fmt$(cost)}).`);
}

export function divingRisk(state, well) {
  const p = stepAt(LOGISTICS.divingFatalPerJobByYear, yf(state)) ?? 0.006;
  if (chance(state, p)) m3Accident(state, 'diving', { well, crew: [], text: 'A saturation diver died on the subsea installation job.', pause: true, extra: { contractorDeaths: 1 } });
}

export function removePlatformImpl(state, platformUid) {
  const p = state.platforms?.[platformUid];
  if (!p || p.status === 'REMOVED') return no('No such platform.');
  const live = [...(p.slotsUsed ?? []), ...(p.hostOf ?? [])].map((id) => state.wells[id]).filter((w) => w && !['PLUGGED', 'DRY'].includes(w.status));
  if (live.length) return no(`Plug ${live.length} well${live.length > 1 ? 's' : ''} on the platform first.`);
  if ((p.rigUids ?? []).some((u) => state.rigs[u]?.wellId)) return no('A rig is still working on the platform.');
  const cost = (p.price ?? 0) * OFFSHORE.decommissionFrac;
  spend(state, cost, 'capex', false);
  if (p.timerId) delete state.timers[p.timerId];
  delete state.platforms[platformUid];
  notify(state, 'platform', { platformUid, fieldId: p.fieldId, phase: 'removed', text: `${eqDef(p.defId)?.name} removed (${fmt$(cost)} decommissioning).` });
  return ok(`Platform decommissioned for ${fmt$(cost)}.`);
}

export function platformsView(state, fieldId = null) {
  return Object.values(state.platforms ?? {}).filter((p) => !fieldId || p.fieldId === fieldId).map((p) => {
    const def = eqDef(p.defId), s = def?.stats ?? {};
    const wells = (p.slotsUsed ?? []).map((id) => state.wells[id]).filter(Boolean);
    const sub = (p.hostOf ?? []).map((id) => state.wells[id]).filter(Boolean);
    const rate = [...wells, ...sub].reduce((a, w) => a + (w.prod?.rate ?? 0), 0);
    const t = p.timerId ? state.timers[p.timerId] : null;
    return {
      uid: p.uid, defId: p.defId, def, name: def?.name, structure: s.platformType, fieldId: p.fieldId, plot: p.plot, status: p.status, waterDepthFt: p.waterDepthFt,
      slots: s.slots ?? 0, slotsUsed: wells.length, rigSlots: s.rigSlots ?? 0, rigs: (p.rigUids ?? []).filter((u) => state.rigs[u]),
      subseaHost: s.subseaHost ?? 0, subseaWells: sub.length, processBbld: s.processBbld ?? 0, rate, utilization: s.processBbld ? rate / s.processBbld : 0,
      storageBbl: s.storageBbl ?? 0, exportMode: s.exportMode ?? 'either', exportOk: exportOk(state, p), designHsFt: s.designHsFt ?? null, manned: !!s.manned, pob: s.pob ?? 0,
      condition: p.condition ?? 100, daysLeft: t?.daysLeft ?? 0, timerId: p.timerId, price: p.price, decommission: Math.round((p.price ?? 0) * OFFSHORE.decommissionFrac),
      wells: wells.map((w) => ({ id: w.id, name: w.name, plot: w.plot, status: w.status, rate: w.prod?.rate ?? 0 })),
    };
  });
}

export const randInt = (state, a, b) => int(state, a, b);
